/**
 * Client Service
 *
 * Handles all client-related operations including CRUD operations,
 * contacts management, and statistics.
 *
 * @module services/clientService
 */

import { supabase } from './supabase';
// #702 — la regla de datos de prueba de este arbol, escrita una sola vez.
// #742/#739 — gemelo web del mismo modulo: el alfabeto y el saneado se
// escriben una vez por ARBOL, no una vez por servicio.
import { terminoParaFiltro, columnaNormalizada, normalizarTexto } from '../utils/normalizacionBusqueda';
import { avisoDatosDePrueba, contarOcultas } from './testDataScope';

/**
 * Get all clients with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.status - Client status
 * @param {string} filters.clientType - Client type
 * @param {string} filters.search - Search term
 * @param {number} filters.page - Page number (0-indexed)
 * @param {number} filters.pageSize - Items per page
 * @returns {Promise<{data, count, error}>}
 */
export const listClients = async (filters = {}) => {
  try {
    // #360 (paridad de carriles con `mcp-server/src/tools/clientTools.js`):
    // los clientes de prueba se excluyen por defecto en los dos carriles.
    const buildQuery = (applyTestFilter) => {
      let query = supabase
        .from('clients')
        .select('*', { count: 'exact' });

      if (applyTestFilter) {
        query = query.eq('is_test_data', false);
      }

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.clientType) {
        query = query.eq('client_type', filters.clientType);
      }

      // #739/#742 — mismo saneado y misma normalizacion que el carril MCP, del
      // mismo modulo. Este es el buscador de clientes que usa Jorge: hasta ahora
      // interpolaba `filters.search` crudo, o sea que un termino con `,` o `()`
      // agregaba ramas al WHERE en vez de buscarse.
      if (filters.search) {
        const termino = terminoParaFiltro(filters.search);
        query = query.or(
          `${columnaNormalizada('company_name')}.ilike.%${termino}%,`
          + `${columnaNormalizada('primary_contact_name')}.ilike.%${termino}%,`
          + `primary_contact_email.ilike.%${termino}%`
        );
      }

      // Apply pagination
      const page = filters.page || 0;
      const pageSize = filters.pageSize || 20;
      const from = page * pageSize;
      const to = from + pageSize - 1;

      return query
        .range(from, to)
        .order('created_at', { ascending: false });
    };

    // La migración 20260727190000 puede no estar aplicada todavía en este
    // entorno. 42703 = undefined_column: se reintenta sin el filtro en vez de
    // dejar la pantalla de clientes en blanco.
    let disponible = true;
    let { data, count, error } = await buildQuery(!filters.includeTestData);

    if (error && error.code === '42703' && /is_test_data/i.test(error.message || '')) {
      console.warn('List clients: columna is_test_data ausente — reintentando sin el filtro (#360; aplicar la migración 20260727190000_clients_is_test_data.sql)');
      disponible = false;
      ({ data, count, error } = await buildQuery(false));
    }

    if (error) throw error;

    // #702 — el mismo silencio que #686, una lista más allá: este carril
    // filtraba desde #360 y devolvía `{ data, count }` sin decir que había
    // sacado a alguien. El contador mide el MISMO universo con el filtro
    // invertido; es fail-open (si no puede contar, `hidden: null`), porque
    // romper la pantalla de clientes por no poder contar lo oculto sería peor
    // que el silencio que esto vino a arreglar.
    let ocultas = 0;
    if (!filters.includeTestData && disponible) {
      ocultas = await contarOcultas(() => {
        let c = supabase.from('clients').select('id', { count: 'exact', head: true })
          .eq('is_test_data', true);
        if (filters.status) c = c.eq('status', filters.status);
        if (filters.clientType) c = c.eq('client_type', filters.clientType);
        // EL MISMO filtro que la pagina: el contador de #686 mide el mismo
        // universo con el filtro invertido, o no mide nada util.
        if (filters.search) {
          const termino = terminoParaFiltro(filters.search);
          c = c.or(
            `${columnaNormalizada('company_name')}.ilike.%${termino}%,`
            + `${columnaNormalizada('primary_contact_name')}.ilike.%${termino}%,`
            + `primary_contact_email.ilike.%${termino}%`
          );
        }
        return c;
      });
    }

    const testDataFilter = avisoDatosDePrueba({
      include: Boolean(filters.includeTestData), ocultas, sujeto: 'clientes', disponible,
    });

    return { data, count, testDataFilter, error: null };
  } catch (error) {
    console.error('List clients error:', error);
    return { data: null, count: 0, error };
  }
};

/**
 * Get client by ID with all related data
 * @param {string} clientId - Client ID
 * @returns {Promise<{data, error}>}
 */
export const getClient = async (clientId) => {
  try {
    // Simple query without FK joins (FK relationships not configured in database)
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', clientId)
      .single();

    if (error) throw error;

    // Fetch related data separately if needed
    let contacts = [];
    let projects = [];

    // Get contacts for this client. `user:profiles(...)` is only populated
    // for contacts linked to a system account (user_id NOT NULL) — a
    // lightweight contact (issue #458, no account) has user_id NULL and the
    // embed comes back null, which is expected, not an error.
    const { data: contactsData } = await supabase
      .from('client_contacts')
      .select('*, user:profiles(id, full_name, phone, role)')
      .eq('client_id', clientId)
      .order('created_at', { ascending: true });

    if (contactsData) contacts = contactsData;

    // Get projects for this client
    const { data: projectsData } = await supabase
      .from('projects')
      .select('id, project_number, title, status, quoted_amount, created_at')
      .eq('client_id', clientId);

    if (projectsData) projects = projectsData;

    return {
      data: {
        ...data,
        contacts,
        projects
      },
      error: null
    };
  } catch (error) {
    console.error('Get client error:', error);
    return { data: null, error };
  }
};

/**
 * Create new client
 * @param {Object} clientData - Client data
 * @param {string} clientData.company_name - Company name
 * @param {string} clientData.legal_id - Legal/tax ID
 * @param {string} clientData.industry - Industry
 * @param {string} clientData.primary_contact_name - Primary contact name
 * @param {string} clientData.primary_contact_email - Primary contact email
 * @param {string} clientData.primary_contact_phone - Primary contact phone
 * @param {string} clientData.address_line1 - Address line 1
 * @param {string} clientData.city - City
 * @param {string} clientData.status - Status
 * @param {boolean} [clientData.is_test_data] - #360: marcar la fila como
 *   fixture de pruebas. EXPLÍCITO y opt-in: sólo lo pasan los harnesses de
 *   test/E2E, nunca el formulario de la UI, y nunca se deriva de NODE_ENV
 *   (ver la cabecera de la migración 20260727190000). Se omite la clave si no
 *   se pidió marcar, para que la creación de clientes reales siga funcionando
 *   en entornos donde la columna todavía no existe.
 * @param {boolean} [clientData.confirm_duplicate] - #642: sólo debe llegar en
 *   true cuando el humano YA confirmó, tras avisarle, que quiere crear un
 *   cliente distinto pese al `company_name` duplicado. Nunca se infiere sola.
 * @returns {Promise<{data, error, duplicate?, duplicateCandidates?}>}
 */
export const createClient = async (clientData) => {
  const { is_test_data, confirm_duplicate, ...rest } = clientData || {};
  const payload = is_test_data === true ? { ...rest, is_test_data: true } : rest;

  // #642 — paridad con mcp-server/src/tools/clientTools.js createClient():
  // no insertar un duplicado en silencio. `legal_id` es IDENTIDAD (se
  // devuelve el existente, nunca se inserta otra fila); `company_name` EXACTO
  // normalizado es AMBIGÜEDAD (se pide confirmación explícita antes de
  // insertar). Ninguna de las dos búsquedas filtra `is_test_data`: el
  // disparador medido del duplicado real de #642 fue justo que la búsqueda
  // previa del agente sí lo filtraba y el cliente sembrado quedó invisible.
  const legalId = payload.legal_id ? String(payload.legal_id).trim() : null;
  if (legalId) {
    const { data: existentePorLegalId } = await supabase
      .from('clients')
      .select('id, company_name, legal_id, is_test_data')
      .eq('legal_id', legalId)
      .limit(1)
      .maybeSingle();
    if (existentePorLegalId) {
      return { data: existentePorLegalId, duplicate: true, error: null };
    }
  }

  if (confirm_duplicate !== true && payload.company_name) {
    const normalizado = normalizarTexto(payload.company_name.trim());
    const { data: candidatos } = await supabase
      .from('clients')
      .select('id, company_name, legal_id, is_test_data')
      .eq(columnaNormalizada('company_name'), normalizado)
      .limit(5);
    if (candidatos && candidatos.length > 0) {
      return { data: null, duplicateCandidates: candidatos, error: null };
    }
  }

  const { data, error } = await supabase
    .from('clients')
    .insert([payload])
    .select()
    .single();

  if (error) throw error;

  return { data, error: null };
};

/**
 * Update client
 * @param {string} clientId - Client ID
 * @param {Object} updates - Client updates
 * @returns {Promise<{data, error}>}
 */
export const updateClient = async (clientId, updates) => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .update(updates)
      .eq('id', clientId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update client error:', error);
    return { data: null, error };
  }
};

/**
 * Delete client
 * @param {string} clientId - Client ID
 * @returns {Promise<{data, error}>}
 */
export const deleteClient = async (clientId) => {
  try {
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', clientId);

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Delete client error:', error);
    return { data: null, error };
  }
};

/**
 * Add contact to client.
 *
 * Two valid shapes (issue #458 — `client_contacts.user_id` is nullable):
 *   1. Existing system user: pass `userId`, omit contact_name/contact_phone.
 *   2. Lightweight contact (no account): pass `userId` as null/undefined and
 *      set `contactData.contact_name` + `contactData.contact_phone` (this is
 *      the common case: a hospital's purchasing contact, not an app login).
 * The DB CHECK constraint (`client_contacts_identity_check`) enforces the
 * same rule server-side; this mirrors it client-side for a clean error
 * instead of a raw Postgres constraint violation.
 *
 * @param {string} clientId - Client ID
 * @param {string|null} userId - Existing profile/user ID, or null for a lightweight contact
 * @param {Object} contactData - Additional contact data
 * @param {string} contactData.contact_role - Contact role (primary/secondary/billing/technical/contact)
 * @param {string} contactData.department - Department
 * @param {string} contactData.job_title - Job title
 * @param {string} contactData.contact_name - Required when userId is null
 * @param {string} contactData.contact_phone - Required when userId is null
 * @param {string} contactData.contact_email - Optional, only meaningful when userId is null
 * @returns {Promise<{data, error}>}
 */
export const addClientContact = async (clientId, userId, contactData = {}) => {
  try {
    if (!userId && (!contactData.contact_name?.trim() || !contactData.contact_phone?.trim())) {
      throw new Error('A contact needs either an existing user or a name and phone');
    }

    const { data, error} = await supabase
      .from('client_contacts')
      .insert([{
        client_id: clientId,
        user_id: userId || null,
        ...contactData,
      }])
      .select(`
        *,
        user:profiles(id, full_name, phone)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Add client contact error:', error);
    return { data: null, error };
  }
};

/**
 * Remove contact from client
 * @param {string} contactId - Contact ID
 * @returns {Promise<{data, error}>}
 */
export const removeClientContact = async (contactId) => {
  try {
    const { error } = await supabase
      .from('client_contacts')
      .delete()
      .eq('id', contactId);

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Remove client contact error:', error);
    return { data: null, error };
  }
};

/**
 * Update client contact
 * @param {string} contactId - Contact ID
 * @param {Object} updates - Contact updates
 * @returns {Promise<{data, error}>}
 */
export const updateClientContact = async (contactId, updates) => {
  try {
    const { data, error } = await supabase
      .from('client_contacts')
      .update(updates)
      .eq('id', contactId)
      .select(`
        *,
        user:profiles(id, full_name, phone)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update client contact error:', error);
    return { data: null, error };
  }
};

/**
 * Get client summary with statistics
 * @param {string} clientId - Client ID
 * @returns {Promise<{data, error}>}
 */
export const getClientSummary = async (clientId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_client_summary', { client_uuid: clientId });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get client summary error:', error);
    return { data: null, error };
  }
};

/**
 * Estados de `projects` que cuentan como "activo" para el cliente.
 *
 * El allowlist real de `projects_status_check` en producción es exactamente:
 *   draft · quotation_sent · quotation_accepted · po_received · in_progress · closed
 *
 * Este cálculo filtraba por `['in_progress', 'pending', 'planning']`. `pending`
 * y `planning` **no existen** — nunca matchearon una fila — así que el número
 * era en realidad "sólo in_progress" e ignoraba `po_received` y
 * `quotation_accepted`, que son trabajo ya comprometido. Filtrar por un valor
 * que el CHECK no admite no da error: da cero en silencio, que se lee como un
 * dato de negocio.
 *
 * Quedan fuera `draft` y `quotation_sent` (todavía no hay compromiso del
 * cliente) y `closed` (terminado).
 */
const ACTIVE_PROJECT_STATUSES = ['quotation_accepted', 'po_received', 'in_progress'];

/**
 * Get client statistics (projects, quotations, revenue)
 * @param {string} clientId - Client ID
 * @returns {Promise<{data, error}>}
 */
export const getClientStats = async (clientId) => {
  try {
    // Get project counts
    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select('id, status, quoted_amount')
      .eq('client_id', clientId);

    if (projectsError) throw projectsError;

    // Get quotation counts via project IDs
    const projectIds = (projects || []).map(p => p.id);
    let quotations = [];
    if (projectIds.length > 0) {
      const { data: quotationsData, error: quotationsError } = await supabase
        .from('quotations')
        .select('id, status, total')
        .in('project_id', projectIds);

      if (quotationsError) throw quotationsError;
      quotations = quotationsData || [];
    }

    // "Facturado" se lee de `invoices`, que es la única tabla donde vive una
    // factura. Antes se sumaba `quoted_amount` de los proyectos con
    // `status === 'completed'`, un estado que **no existe** en
    // `projects_status_check`: la tarjeta marcaba $0 para todo cliente, incluso
    // con proyectos cerrados y facturas emitidas. Y aunque el estado hubiera
    // existido, un monto cotizado no es un monto facturado.
    //
    // Se buscan las facturas del cliente por sus dos vías reales: `client_id`
    // directo y, para las filas viejas que sólo traen `project_id`, por los
    // proyectos de este cliente. `.or()` devuelve cada fila una sola vez.
    let invoices = [];
    {
      let invoiceQuery = supabase.from('invoices').select('id, amount');
      invoiceQuery = projectIds.length > 0
        ? invoiceQuery.or(`client_id.eq.${clientId},project_id.in.(${projectIds.join(',')})`)
        : invoiceQuery.eq('client_id', clientId);

      const { data: invoicesData, error: invoicesError } = await invoiceQuery;
      if (invoicesError) throw invoicesError;
      invoices = invoicesData || [];
    }

    // Calculate stats
    const totalProjects = projects?.length || 0;
    const activeProjects = projects?.filter(p =>
      ACTIVE_PROJECT_STATUSES.includes(p.status)
    ).length || 0;
    const totalQuotations = quotations?.length || 0;
    const totalRevenue = invoices.reduce((sum, inv) => sum + Number(inv.amount || 0), 0);

    return {
      data: {
        total_projects: totalProjects,
        active_projects: activeProjects,
        total_quotations: totalQuotations,
        total_revenue: totalRevenue,
      },
      error: null,
    };
  } catch (error) {
    console.error('Get client stats error:', error);
    return { data: null, error };
  }
};
